import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveReviewAutomationSettings } from "../api/saveReviewAutomationSettings";
import {
  ReviewAutomationSettingsPayload,
  ReviewAutomationSettingsResponse
} from "../../types/review-automation.type";

interface UseSaveReviewAutomationSettingsProps {
  onSuccess?: (data: ReviewAutomationSettingsResponse) => void;
  onError?: (error: any) => void;
}

/**
 * Custom React Query mutation hook to save review automation settings.
 *
 * @param props - Optional callbacks for onSuccess and onError, and optional siteIdOverride
 * @returns Object containing the mutation state, handlers, and status flags
 */
export function useSaveReviewAutomationSettings({
  onSuccess,
  onError,
}: UseSaveReviewAutomationSettingsProps = {}) {
  const queryClient = useQueryClient();

  const saveSettingsMutation = useMutation({
    mutationFn: (payload: ReviewAutomationSettingsPayload) =>
      saveReviewAutomationSettings({
        site_id: payload.site_id,
        ...payload,
      }),
    mutationKey: ["saveReviewAutomationSettings"],
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["getReviewAutomationDashboard"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getReviewAutomationSettings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["reviewAutomationSettings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUserAgentDetails"],
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    saveSettingsMutation,
    saveSettings: saveSettingsMutation.mutate,
    saveSettingsAsync: saveSettingsMutation.mutateAsync,
    isLoading: saveSettingsMutation.isPending,
    isSuccess: saveSettingsMutation.isSuccess,
    isError: saveSettingsMutation.isError,
    error: saveSettingsMutation.error,
  };
}

export default useSaveReviewAutomationSettings;
